import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './index.module.scss';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Translate, translate } from '@utils/i18n';
import { MdDragIndicator } from 'react-icons/md';
import { IoTrashOutline } from 'react-icons/io5';
import { AddMetricModal } from '_clean-code/pages/UIConfigScreen/components/AddMetricModal';
import _ from 'lodash';
import { Button, Text } from '@pulsarml/avalon-ui';
import { PlusIcon } from '../icons/PlusIcon';
import { MetricWithNamedType } from '../../MachineMetricsComponent';
import { SquarePen } from 'lucide-react';

export interface DraggableListComponentProps {
  onChange: (newItems: MetricWithNamedType[]) => void;
  metrics: MetricWithNamedType[];
  metricsCatalogue: MetricWithNamedType[];
  maxNumberOfMetrics?: number;
}

export function DraggableListComponent({
  onChange,
  metrics,
  metricsCatalogue,
  maxNumberOfMetrics
}: DraggableListComponentProps) {
  const [items, setItems] = useState<MetricWithNamedType[]>(
    _.cloneDeep(metrics)
  );
  const [showAddMetricModal, setShowAddMetricModal] = useState(false);
  const [editingParameter, setEditingParameter] = useState<{
    metricId: string;
    parameterIndex: number;
  } | null>(null);
  const metricNameReformatList = ['annotation', 'total', 'correct', 'defects'];

  useEffect(() => {
    if (_.isEqual(items, metrics)) return;
    onChange(items);
  }, [items, metrics, onChange]);

  const handleConfirmEdit = (metricId: string, paramIndex: number) => {
    setItems((currentItems) =>
      currentItems.map((item) => {
        if (item.metric === metricId) {
          const newParameters = item.parameters?.map((p, pIdx) => {
            if (pIdx === paramIndex) {
              const paramToValidate = p;
              const currentValueAsString = String(paramToValidate.value);
              let finalValue = currentValueAsString;

              const attemptedNumericValue = parseFloat(currentValueAsString);

              if (!isNaN(attemptedNumericValue)) {
                let validatedNumericValue = attemptedNumericValue;
                const min = paramToValidate.min;
                const max = paramToValidate.max;

                if (typeof max === 'number' && validatedNumericValue > max) {
                  validatedNumericValue = max;
                }
                if (typeof min === 'number' && validatedNumericValue < min) {
                  validatedNumericValue = min;
                }
                finalValue = String(validatedNumericValue);
              } else {
                // If not a number, default to min if it exists, otherwise empty string
                const min = paramToValidate.min;
                if (typeof min === 'number') {
                  finalValue = String(min);
                } else {
                  finalValue = '';
                }
              }
              return { ...paramToValidate, value: finalValue };
            }
            return p;
          });
          return { ...item, parameters: newParameters };
        }
        return item;
      })
    );
    setEditingParameter(null);
  };

  return (
    <>
      <DragDropContext
        onDragEnd={(result) => {
          const destinationIndex = result?.destination?.index;
          const sourceIndex = result?.source?.index;
          if (sourceIndex != null && destinationIndex != null) {
            setItems((_items) => {
              const [source] = _items.splice(sourceIndex, 1);
              _items.splice(destinationIndex, 0, source);
              return [..._items].map((x, i) => ({ ...x, order: i }));
            });
          }
        }}
      >
        <div>
          <Droppable droppableId="list">
            {(provided) => (
              <div
                className={styles.metricsContainer}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {items.map((metric, i) => (
                  <Draggable
                    draggableId={metric.metric}
                    index={i}
                    key={metric.metric}
                  >
                    {(e) => (
                      <div
                        className={cn(styles.metricContainer)}
                        ref={e.innerRef}
                        {...e.draggableProps}
                      >
                        <div className={cn(styles.metricBox)}>
                          <div className={styles.metricDetail}>
                            <Text variant="label" className="tw-text-base">
                              {i + 1}.{' '}
                              <Translate
                                value={
                                  metricNameReformatList.includes(metric.metric)
                                    ? `${metric.metric}_metric`
                                    : metric.metric
                                }
                              />
                            </Text>
                            {metric.parameters &&
                              metric.parameters.length > 0 && (
                                <div className={styles.divider} />
                              )}
                            {metric.parameters &&
                              metric.parameters.map((parameter, paramIndex) => {
                                if (parameter.name === 'view_by') return null;

                                const isCurrentlyEditingThisParam =
                                  editingParameter &&
                                  editingParameter.metricId === metric.metric &&
                                  editingParameter.parameterIndex ===
                                    paramIndex &&
                                  parameter.name === 'average_on';

                                return (
                                  <div
                                    className={cn(styles.pill, 'px-1')}
                                    key={paramIndex}
                                  >
                                    <Translate value={parameter.name} />
                                    &nbsp;=&nbsp;
                                    {isCurrentlyEditingThisParam ? (
                                      <input
                                        type="text"
                                        value={parameter.value}
                                        onChange={(e) => {
                                          const newValue = e.target.value;
                                          setItems((currentItems) =>
                                            currentItems.map((item) => {
                                              if (
                                                item.metric === metric.metric
                                              ) {
                                                return {
                                                  ...item,
                                                  parameters:
                                                    item.parameters?.map(
                                                      (p, pIdx) => {
                                                        if (
                                                          pIdx === paramIndex
                                                        ) {
                                                          return {
                                                            ...p,
                                                            value: newValue
                                                          };
                                                        }
                                                        return p;
                                                      }
                                                    )
                                                };
                                              }
                                              return item;
                                            })
                                          );
                                        }}
                                        onBlur={() => {
                                          handleConfirmEdit(
                                            metric.metric,
                                            paramIndex
                                          );
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleConfirmEdit(
                                              metric.metric,
                                              paramIndex
                                            );
                                          }
                                        }}
                                        // eslint-disable-next-line jsx-a11y/no-autofocus
                                        autoFocus
                                        style={{
                                          width: '60px',
                                          textAlign: 'center',
                                          border: '1px solid #ccc',
                                          borderRadius: '4px',
                                          padding: '2px 4px',
                                          fontSize: 'inherit'
                                        }}
                                      />
                                    ) : (
                                      <>
                                        {parameter.type === 'option' ? (
                                          <Translate
                                            value={parameter.value + '_option'}
                                          />
                                        ) : (
                                          parameter.value
                                        )}
                                      </>
                                    )}
                                    {parameter.name === 'average_on' && (
                                      <>
                                        &nbsp;
                                        {_.lowerCase(translate('minutes'))}
                                        {!isCurrentlyEditingThisParam && (
                                          <SquarePen
                                            className="ml-1"
                                            size={14}
                                            role="button"
                                            onClick={() => {
                                              setEditingParameter({
                                                metricId: metric.metric,
                                                parameterIndex: paramIndex
                                              });
                                            }}
                                          />
                                        )}
                                      </>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                          {items.length > 1 && (
                            <IoTrashOutline
                              size={20}
                              color="#53646f"
                              role="button"
                              onClick={() => {
                                setItems((_items) => {
                                  const newItems = [..._items];
                                  const index = newItems.findIndex(
                                    (x) => x.metric === metric.metric
                                  );
                                  newItems.splice(index, 1);
                                  onChange([...newItems]);
                                  return [...newItems];
                                });
                              }}
                            />
                          )}
                          <div {...e.dragHandleProps}>
                            <MdDragIndicator size={20} role="button" />
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {maxNumberOfMetrics && items.length >= maxNumberOfMetrics ? (
                  <></>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowAddMetricModal(true);
                    }}
                  >
                    <PlusIcon /> <Translate value="add_metric" />
                  </Button>
                )}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
      {showAddMetricModal && (
        <AddMetricModal
          metricsCatalogue={metricsCatalogue}
          onConfirm={(newConfigs) => {
            setItems((items) => {
              return [
                ...items,
                ...newConfigs.map((x, i) => ({ ...x, order: items.length + i }))
              ];
            });
            setShowAddMetricModal(false);
          }}
          onClose={() => {
            setShowAddMetricModal(false);
          }}
        />
      )}
    </>
  );
}
